import { extractErrorMessage } from "@platform/api";
import {
  createFieldComment,
  deleteFieldComment,
  updateFieldComment,
  upsertFieldComments,
} from "@platform/plan-grid-core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";

import { isRateFieldPath } from "../utils/rateFieldPath";

import { benefitPlanGroupQueryKeys } from "./useBenefitPlanGroups";

import type { AiRateFieldsMeta, BenefitPlanGroup } from "../types/models";
import type { RateReviewTarget } from "../utils/rateReviewTarget";
import type {
  BenefitPlanFieldCommentsRequest,
  CellReview,
  FieldCommentResponse,
  UpdateFieldCommentRequest,
  UpsertFieldCommentsResponse,
} from "@platform/plan-grid-core";
import type { AxiosInstance } from "axios";

/**
 * A rate cell's row as this store reads it. BTX rows also carry the "Edited"
 * notation, which the shared review contract reads through `getEdited`.
 */
type RateCellReview = CellReview & { manuallyEdited?: boolean };

type RateReviewIndex = Record<string, RateCellReview>;

/**
 * Rate-cell state written but not yet read back from the server. Once the
 * write settles, the entry keeps showing until the server index it settled
 * against is replaced by a newer read.
 */
interface PendingReview {
  review: RateCellReview | null;
  settledAgainst?: RateReviewIndex;
}

type PendingReviews = Record<string, PendingReview>;

interface RateCell {
  planId: string;
  fieldPath: string;
}

/** Edited marks gathered in one tick, sent together in a single upsert. */
interface EditBatch {
  cells: Map<string, RateCell>;
  previousRowIds: Map<string, Promise<string | undefined>>;
  written: Promise<UpsertFieldCommentsResponse>;
  owner: symbol;
}

const indexKey = (planId: string, fieldPath: string): string =>
  `${planId}::${fieldPath}`;

const serverShowsWrite = (
  server: RateCellReview | undefined,
  written: RateCellReview | null,
): boolean =>
  written === null
    ? server === undefined
    : server !== undefined &&
      Boolean(server.isValidated) === Boolean(written.isValidated) &&
      Boolean(server.needsReview) === Boolean(written.needsReview) &&
      Boolean(server.manuallyEdited) === Boolean(written.manuallyEdited) &&
      (server.comment ?? "") === (written.comment ?? "");

/** The part of a field comment the cell-review contract consumes. */
const toCellReview = (comment: FieldCommentResponse): RateCellReview => ({
  id: comment.id,
  comment: comment.comment,
  needsReview: comment.needsReview,
  isValidated: comment.isValidated,
  manuallyEdited: comment.manuallyEdited,
});

/**
 * Indexes the rate-area rows already embedded in the benefit-plan-group read.
 *
 * Every rate-path row is indexed, whatever it currently holds: one row carries
 * the review, the validation and the edited mark of a cell, and a row can
 * outlive all three. The readers decide what each flag means.
 */
const indexRateReviews = (
  benefitPlanGroups: BenefitPlanGroup[],
): RateReviewIndex =>
  Object.fromEntries(
    benefitPlanGroups.flatMap((group) =>
      group.benefitPlans.flatMap((plan) =>
        (plan.fieldComments ?? [])
          .filter((comment) => isRateFieldPath(comment.fieldPath))
          .map((comment): [string, RateCellReview] => [
            indexKey(plan.id, comment.fieldPath),
            toCellReview(comment),
          ]),
      ),
    ),
  );

const visibleReview = (
  entry: PendingReview | undefined,
  server: RateReviewIndex,
  key: string,
): RateCellReview | undefined => {
  const showsPending =
    entry !== undefined &&
    (entry.settledAgainst === undefined || entry.settledAgainst === server);
  return (showsPending ? entry.review : server[key]) ?? undefined;
};

/** Groups a batch's cells into the per-plan shape the upsert endpoint takes. */
const toEditedUpserts = (
  cells: Iterable<RateCell>,
): BenefitPlanFieldCommentsRequest[] =>
  Object.entries(
    Array.from(cells).reduce<Record<string, string[]>>(
      (byPlan, { planId, fieldPath }) => ({
        ...byPlan,
        [planId]: [...(byPlan[planId] ?? []), fieldPath],
      }),
      {},
    ),
  ).map(([benefitPlanId, fieldPaths]) => ({
    benefitPlanId,
    fields: fieldPaths.map((fieldPath) => ({
      fieldPath,
      manuallyEdited: true,
    })),
  }));

const findUpserted = (
  written: UpsertFieldCommentsResponse,
  { planId, fieldPath }: RateCell,
): FieldCommentResponse | undefined =>
  written.benefitPlans
    .find((plan) => plan.benefitPlanId === planId)
    ?.fieldComments.find((comment) => comment.fieldPath === fieldPath);

export interface BuildRateCellReviews {
  getReview: (planId: string, fieldPath: string) => CellReview | undefined;
  upsertReview: (
    planId: string,
    fieldPath: string,
    comment: string | null,
  ) => void;
  clearReview: (planId: string, fieldPath: string) => void;
  setValidated: (targets: RateReviewTarget[], isValidated: boolean) => void;
  setComment: (planId: string, fieldPath: string, comment: string) => void;
  getEdited: (planId: string, fieldPath: string) => boolean;
  setEdited: (planId: string, fieldPath: string) => void;
}

interface UseBuildRateCellReviewsOptions {
  benefitPlanGroups: BenefitPlanGroup[];
  axiosInstance: AxiosInstance;
  /** BTX RFP the plans belong to; the bulk field-comment write is scoped to it. */
  rfpId: string;
  /**
   * AI extraction hints per plan. Only a plan whose rates carry one takes the
   * "Edited" mark: it records that the card no longer holds what the AI
   * extracted. That holds even when the extraction left the card empty, so a
   * paste onto such a plan is marked; a plan the AI never touched is not.
   */
  aiFieldsByPlanId?: Record<string, AiRateFieldsMeta>;
  onSaveError?: (message: string) => void;
}

/**
 * Backs the Build tab's rate-cell reviews, validations and edited marks with
 * BTX field comments.
 *
 * Rate cells reuse the `benefit_field_comments` row that plan-benefit cells
 * already use, under a `rates::` field path. The two namespaces never address
 * the same row, so this store and the plan designer's own can run side by side.
 *
 * Reviews arrive embedded in the benefit-plan-group read, so there is no
 * separate fetch; writes show immediately and are reconciled by refetching that
 * read once the server confirms them.
 */
export const useBuildRateCellReviews = ({
  benefitPlanGroups,
  axiosInstance,
  rfpId,
  aiFieldsByPlanId,
  onSaveError,
}: UseBuildRateCellReviewsOptions): BuildRateCellReviews => {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingReviews>({});
  /**
   * The last write still in flight on each cell, resolving to the cell's row id
   * once it lands. Every write starts from the previous one's result, so writes
   * to a cell reach the server in the order they were made, and a cell created a
   * moment ago is patched rather than created twice.
   */
  const inFlightWrites = useRef(new Map<string, Promise<string | undefined>>());
  /**
   * The edited marks still being gathered this tick. A paste marks every cell
   * of a rates card at once, and one upsert keeps that to a single request and
   * a single refetch.
   */
  const editBatch = useRef<EditBatch | null>(null);

  const pendingOwners = useRef(new Map<string, symbol>());

  const claimPending = useCallback(
    (keys: string[], owner: symbol = Symbol("pending write")): symbol => {
      keys.forEach((key) => pendingOwners.current.set(key, owner));
      return owner;
    },
    [],
  );

  const serverReviews = useMemo(
    () => indexRateReviews(benefitPlanGroups),
    [benefitPlanGroups],
  );

  const serverReviewsRef = useRef(serverReviews);
  serverReviewsRef.current = serverReviews;

  const readReview = useCallback(
    (planId: string, fieldPath: string): RateCellReview | undefined => {
      const key = indexKey(planId, fieldPath);
      return visibleReview(pending[key], serverReviews, key);
    },
    [pending, serverReviews],
  );

  /**
   * Shows a change immediately, applied over the cell's newest known state so
   * that two writes landing in one tick keep each other's flags.
   */
  const showPending = useCallback(
    (key: string, changes: Partial<RateCellReview>) =>
      setPending((current) => ({
        ...current,
        [key]: {
          review: {
            ...visibleReview(current[key], serverReviewsRef.current, key),
            ...changes,
          },
        },
      })),
    [],
  );

  /**
   * Hands each owned cell back to the server read. On failure the cell reverts
   * at once; on success it keeps its written state until a newer server read
   * arrives, since the refetch lands in the cache before the grid re-renders.
   */
  const settleMany = useCallback(
    async (keys: string[], owner: symbol, error?: unknown) => {
      if (error) {
        onSaveError?.(extractErrorMessage(error));
      } else {
        await queryClient.invalidateQueries({
          queryKey: benefitPlanGroupQueryKeys.all,
        });
      }

      const ownedKeys = keys.filter(
        (key) => pendingOwners.current.get(key) === owner,
      );
      ownedKeys.forEach((key) => {
        pendingOwners.current.delete(key);
        inFlightWrites.current.delete(key);
      });

      const settledAgainst = serverReviewsRef.current;
      setPending((current) =>
        Object.fromEntries(
          Object.entries(current).flatMap(
            ([pendingKey, entry]): [string, PendingReview][] => {
              const isExpired =
                entry.settledAgainst !== undefined &&
                entry.settledAgainst !== settledAgainst;
              if (isExpired) return [];
              if (!ownedKeys.includes(pendingKey)) return [[pendingKey, entry]];

              const isWaitingForServer =
                !error &&
                !serverShowsWrite(settledAgainst[pendingKey], entry.review);
              return isWaitingForServer
                ? [[pendingKey, { ...entry, settledAgainst }]]
                : [];
            },
          ),
        ),
      );
    },
    [onSaveError, queryClient],
  );

  const settle = useCallback(
    (key: string, owner: symbol, error?: unknown) =>
      settleMany([key], owner, error),
    [settleMany],
  );

  const rowIdBefore = useCallback(
    (
      key: string,
      knownRowId: string | undefined,
    ): Promise<string | undefined> =>
      inFlightWrites.current.get(key) ?? Promise.resolve(knownRowId),
    [],
  );

  const trackWrite = useCallback(
    (
      key: string,
      previousRowId: Promise<string | undefined>,
      rowIdAfter: Promise<string | undefined>,
    ) => {
      inFlightWrites.current.set(
        key,
        rowIdAfter.then(
          (rowId) => rowId,
          () => previousRowId,
        ),
      );
    },
    [],
  );

  /**
   * Applies one change to a cell: shows it immediately, then persists it after
   * any write still in flight on the cell, creating the row when there is none.
   */
  const write = useCallback(
    (
      planId: string,
      fieldPath: string,
      patch: UpdateFieldCommentRequest,
      changes: Partial<RateCellReview>,
    ) => {
      const key = indexKey(planId, fieldPath);
      const previousRowId = rowIdBefore(key, readReview(planId, fieldPath)?.id);

      const owner = claimPending([key]);
      showPending(key, changes);

      const rowIdAfter = previousRowId.then(async (rowId) => {
        if (rowId) {
          await updateFieldComment(planId, rowId, patch, axiosInstance);
          return rowId;
        }

        const created = await createFieldComment(
          planId,
          { fieldPath, ...patch },
          axiosInstance,
        );
        return created.id;
      });
      trackWrite(key, previousRowId, rowIdAfter);

      rowIdAfter.then(
        () => settle(key, owner),
        (error: unknown) => settle(key, owner, error),
      );
    },
    [
      axiosInstance,
      claimPending,
      readReview,
      rowIdBefore,
      settle,
      showPending,
      trackWrite,
    ],
  );

  /**
   * Adds a cell to this tick's edited batch, opening the batch if none is. The
   * batch waits for any write still in flight on its cells, and joins each
   * cell's queue, so a write after it patches the row the upsert made.
   */
  const enqueueEdited = useCallback(
    (cell: RateCell) => {
      if (!editBatch.current) {
        const cells = new Map<string, RateCell>();
        const previousRowIds = new Map<string, Promise<string | undefined>>();
        const owner = Symbol("edited batch");
        const written = Promise.resolve().then(async () => {
          editBatch.current = null;
          await Promise.all(previousRowIds.values());
          return upsertFieldComments(
            rfpId,
            { benefitPlans: toEditedUpserts(cells.values()) },
            axiosInstance,
          );
        });

        written.then(
          () => settleMany([...cells.keys()], owner),
          (error: unknown) => settleMany([...cells.keys()], owner, error),
        );
        editBatch.current = { cells, previousRowIds, written, owner };
      }

      const batch = editBatch.current;
      const key = indexKey(cell.planId, cell.fieldPath);
      const previousRowId = rowIdBefore(
        key,
        readReview(cell.planId, cell.fieldPath)?.id,
      );

      batch.cells.set(key, cell);
      batch.previousRowIds.set(key, previousRowId);
      claimPending([key], batch.owner);
      trackWrite(
        key,
        previousRowId,
        batch.written.then((written) => findUpserted(written, cell)?.id),
      );
    },
    [
      axiosInstance,
      claimPending,
      readReview,
      rfpId,
      rowIdBefore,
      settleMany,
      trackWrite,
    ],
  );

  const upsertReview = useCallback(
    (planId: string, fieldPath: string, comment: string | null) => {
      // The field-comment endpoint reads an absent comment as "leave it alone",
      // and a `*string` cannot tell JSON null from absent — so null would keep
      // the old text. An empty string is what clears it.
      const storedComment = comment ?? "";

      // Flagging a cell undoes its validation: the two name opposite states.
      write(
        planId,
        fieldPath,
        { comment: storedComment, needsReview: true, isValidated: false },
        { comment, needsReview: true, isValidated: false },
      );
    },
    [write],
  );

  const clearReview = useCallback(
    (planId: string, fieldPath: string) => {
      const existing = readReview(planId, fieldPath);

      // The row also carries the validated flag and the edited mark, so a cell
      // holding either keeps its row and loses only the review.
      if (existing?.isValidated || existing?.manuallyEdited) {
        write(
          planId,
          fieldPath,
          { comment: "", needsReview: false },
          { comment: null, needsReview: false },
        );
        return;
      }

      const key = indexKey(planId, fieldPath);
      const previousRowId = rowIdBefore(key, existing?.id);

      const owner = claimPending([key]);
      setPending((current) => ({ ...current, [key]: { review: null } }));

      const rowIdAfter = previousRowId.then(async (rowId) => {
        if (rowId) {
          await deleteFieldComment(planId, rowId, axiosInstance);
        }
        return undefined;
      });
      trackWrite(key, previousRowId, rowIdAfter);

      rowIdAfter.then(
        () => settle(key, owner),
        (error: unknown) => settle(key, owner, error),
      );
    },
    [
      axiosInstance,
      claimPending,
      readReview,
      rowIdBefore,
      settle,
      trackWrite,
      write,
    ],
  );

  const setValidated = useCallback(
    (targets: RateReviewTarget[], isValidated: boolean) => {
      const changes = targets.flatMap(({ planId, fieldPath }) => {
        const existing = readReview(planId, fieldPath);
        const isAlreadySet = existing
          ? Boolean(existing.isValidated) === isValidated
          : !isValidated;
        if (isAlreadySet) return [];

        // Validating settles the cell, so it drops out of review, and clearing
        // the flag puts a cell that kept a comment back into it. A comment never
        // stands on a cell that carries neither flag. Either way the text itself
        // is left untouched.
        const needsReview = isValidated ? false : Boolean(existing?.comment);
        return [
          {
            planId,
            fieldPath,
            key: indexKey(planId, fieldPath),
            rowId: existing?.id,
            needsReview,
          },
        ];
      });
      if (changes.length === 0) return;

      const keys = changes.map(({ key }) => key);
      const owner = claimPending(keys);
      changes.forEach(({ key, needsReview }) =>
        showPending(key, { needsReview, isValidated }),
      );

      const fieldsByPlan = changes.reduce(
        (byPlan, { planId, fieldPath, needsReview }) =>
          byPlan.set(planId, [
            ...(byPlan.get(planId) ?? []),
            { fieldPath, needsReview, isValidated },
          ]),
        new Map<
          string,
          { fieldPath: string; needsReview: boolean; isValidated: boolean }[]
        >(),
      );

      const previousRowIds = changes.map(({ key, rowId }) =>
        rowIdBefore(key, rowId),
      );
      const upsert = Promise.all(previousRowIds).then(() =>
        upsertFieldComments(
          rfpId,
          {
            benefitPlans: [...fieldsByPlan].map(([benefitPlanId, fields]) => ({
              benefitPlanId,
              fields,
            })),
          },
          axiosInstance,
        ),
      );

      changes.forEach(({ planId, fieldPath, key }, idx) =>
        trackWrite(
          key,
          previousRowIds[idx],
          upsert.then(
            (written) => findUpserted(written, { planId, fieldPath })?.id,
          ),
        ),
      );

      upsert.then(
        () => settleMany(keys, owner),
        (error: unknown) => settleMany(keys, owner, error),
      );
    },
    [
      axiosInstance,
      claimPending,
      readReview,
      rfpId,
      rowIdBefore,
      settleMany,
      showPending,
      trackWrite,
    ],
  );

  const setComment = useCallback(
    (planId: string, fieldPath: string, comment: string) => {
      if (!readReview(planId, fieldPath)) return;

      // Only the text changes: the cell keeps whichever flag it holds.
      write(planId, fieldPath, { comment }, { comment });
    },
    [readReview, write],
  );

  const getEdited = useCallback(
    (planId: string, fieldPath: string): boolean =>
      Boolean(readReview(planId, fieldPath)?.manuallyEdited),
    [readReview],
  );

  const setEdited = useCallback(
    (planId: string, fieldPath: string) => {
      const planHasAiRates = Boolean(aiFieldsByPlanId?.[planId]);
      if (!planHasAiRates || getEdited(planId, fieldPath)) return;

      showPending(indexKey(planId, fieldPath), { manuallyEdited: true });
      enqueueEdited({ planId, fieldPath });
    },
    [aiFieldsByPlanId, enqueueEdited, getEdited, showPending],
  );

  return {
    getReview: readReview,
    upsertReview,
    clearReview,
    setValidated,
    setComment,
    getEdited,
    setEdited,
  };
};
